/**
 * Dietitian Schedule Form Component (Step 3)
 *
 * Schedule setup form for dietitian registration workflow.
 * Includes Calendly OAuth integration for appointment scheduling.
 *
 * @module components/dietician-registration/DieticianScheduleForm
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { useDieticianScheduleForm, type DieticianScheduleFormData } from '@/hooks/useDieticianScheduleForm';
import { getLogger } from '@/services/shared/LoggerFactory';

/**DieticianScheduleForm
 * Component props interface
 */
export interface DieticianScheduleFormProps {
  onSubmit: (formData: DieticianScheduleFormData) => Promise<void>;
  initialData?: Partial<DieticianScheduleFormData>;
  isLoading: boolean;
  errors: Record<string, string>;
}

/**
 * DieticianScheduleForm Component
 *
 * Step 3 of the dietitian registration workflow.
 * Handles Calendly OAuth integration for appointment scheduling.
 */
export const DieticianScheduleForm: React.FC<DieticianScheduleFormProps> = ({
  onSubmit,
  initialData,
  isLoading,
  errors,
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();

  // Use the custom hook to manage form state and logic
  const {
    formData,
    showLoadingOverlay,
    isFormValid,
    hasValidationError,
    validationState,
    shouldShowValidationErrors,
    handleCalendlyConnect,
    handleSubmit,
    fieldRefs
  } = useDieticianScheduleForm({
    onSubmit,
    initialData
  });


  logger.debug('isFormValid', isFormValid);
  logger.debug('formData.calendlySchedulingUr,', formData.calendlySchedulingUri);

  return (
    <>
      {/* Loading Overlay */}
      {showLoadingOverlay && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center space-y-4 shadow-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className={cn(
              'text-sm font-medium',
              isDark ? 'text-gray-200' : 'text-gray-700'
            )}>
              Connecting to Calendly...
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
      {/* Form error display */}
      {errors.form && (
        <div className={cn(
          'p-4 rounded-lg border',
          isDark
            ? 'bg-red-900/20 border-red-400 text-red-300'
            : 'bg-red-50 border-red-200 text-red-800'
        )}>
          <p className="text-sm font-medium">{errors.form}</p>
        </div>
      )}

      {/* Introduction */}
      <div className="space-y-2">
        <p className={cn(
          'text-sm',
          isDark ? 'text-secondary-300' : 'text-secondary-600'
        )}>
          Connect your Calendly account to allow patients to book appointments with you directly.
          This integration will enable seamless scheduling through the BiteWise platform.
        </p>
      </div>

      {/* Calendly OAuth Connection */}
      <div className="space-y-4">
        <div className="flex flex-col items-center space-y-2">
          <Button
            ref={(el) => { fieldRefs.current.calendlyConnection = el; }}
            variant="outline"
            type="button"
            size="md"
            onClick={handleCalendlyConnect}
            disabled={isLoading}
            aria-label="Connect with Calendly to enable appointment scheduling"
          >
            Connect to Calendly
          </Button>

          {/* Show validation error message */}
          {hasValidationError && !isFormValid && shouldShowValidationErrors && validationState['calendlyConnection']?.result.error && (
            <p className={cn(
              'text-sm font-medium',
              isDark ? 'text-red-400' : 'text-red-600'
            )}>
              {validationState['calendlyConnection'].result.error}
            </p>
          )}
        </div>

        {(formData.calendlySchedulingUri !== undefined && !isFormValid) ? (
          <div className="space-y-4">

            <div className={cn(
              'p-4 rounded-lg border',
              isDark ? 'bg-blue-900/20 border-blue-400 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-800'
            )}>
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="space-y-1">
                  <p className="text-sm font-medium">Secure OAuth Connection</p>
                  <p className="text-sm">
                    Clicking "Connect with Calendly" will open a secure window where you can authorize
                    BiteWise to access your Calendly account for appointment scheduling.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={cn(
            'p-4 rounded-lg border',
            isDark ? 'bg-green-900/20 border-green-400 text-green-300' : 'bg-green-50 border-green-200 text-green-800'
          )}>
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-sm font-medium">Calendly Connected Successfully</span>
            </div>
            <p className="text-sm mt-2">
              Your Calendly account is now connected. Patients will be able to book appointments
              with you through the BiteWise platform.
            </p>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className={`flex justify-end pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <Button
          type="submit"
          disabled={isLoading}
          className="min-w-[120px]"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Completing...</span>
            </div>
          ) : (
            'Complete Registration'
          )}
        </Button>
      </div>
    </form>
    </>
  );
};

export default DieticianScheduleForm;